import React, { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import { DemoFillBanner } from '../ui/DemoFillBanner';
import { buildSampleDocuments } from '../../constants/sampleData';
type BusinessType = 'individual' | 'sole_proprietor' | 'partnership' | 'corporation' | 'non_profit' | '';
interface DocumentsJourneyProps {
  businessType?: BusinessType;
  /** Identity method chosen on the Representative Details screen. */
  idMethod?: 'national_id' | 'passport';
  onComplete: (data: DocumentsState) => void;
  onExit: () => void;
  initialData?: Partial<DocumentsState>;
}
interface DocumentsState {
  idFront: File | null;
  idBack: File | null;
  bizDoc1: File | null;
  bizDoc2: File | null;
}
interface FormGroupProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}
function FormGroup({
  title,
  subtitle,
  children
}: FormGroupProps) {
  return <fieldset className="space-y-5" data-id="element-177">
      <div data-id="element-178">
        <legend className="text-base font-bold uppercase tracking-wider text-gray-900" data-id="element-179">
          {title}
        </legend>
        {subtitle && <p className="text-sm text-gray-600 mt-1.5" data-id="element-180">{subtitle}</p>}
      </div>
      {children}
    </fieldset>;
}
export function DocumentsJourney({
  businessType = '',
  idMethod,
  onComplete,
  onExit,
  initialData
}: DocumentsJourneyProps) {
  const [idFront, setIdFront] = useState<File | null>(initialData?.idFront ?? null);
  const [idBack, setIdBack] = useState<File | null>(initialData?.idBack ?? null);
  const [bizDoc1, setBizDoc1] = useState<File | null>(initialData?.bizDoc1 ?? null);
  const [bizDoc2, setBizDoc2] = useState<File | null>(initialData?.bizDoc2 ?? null);
  // Step management
  type Step = 'identity' | 'business';
  const [currentStep, setCurrentStep] = useState<Step>('identity');
  // Conditional config based on business type
  const businessDocs = useMemo(() => {
    switch (businessType) {
      case 'sole_proprietor':
      case 'partnership':
        return {
          show: true,
          doc1Label: 'SSM Business Registration Certificate (Form D / Form E)',
          doc2Label: 'SSM Business Info (Printout)',
          doc2Required: true
        };
      case 'corporation':
        return {
          show: true,
          doc1Label: 'Certificate of Incorporation (Section 17 or Form 9)',
          doc2Label: 'Register of Directors / Shareholders (Section 58 & 51, or Form 49 & 24)',
          doc2Required: true
        };
      case 'non_profit':
        return {
          show: true,
          doc1Label: 'Certificate of Registration (Registry of Societies / ROS)',
          doc2Label: '',
          doc2Required: false
        };
      case 'individual':
      default:
        return {
          show: false,
          doc1Label: '',
          doc2Label: '',
          doc2Required: false
        };
    }
  }, [businessType]);
  const isIdentityValid = useMemo(() => {
    if (!idFront) return false;
    if (idMethod !== 'passport' && !idBack) return false;
    return true;
  }, [idFront, idBack, idMethod]);
  const isBusinessValid = useMemo(() => {
    if (!businessDocs.show) return true;
    if (!bizDoc1) return false;
    if (businessDocs.doc2Required && !bizDoc2) return false;
    return true;
  }, [bizDoc1, bizDoc2, businessDocs]);
  const isCurrentStepValid = () => {
    if (currentStep === 'identity') return isIdentityValid;
    if (currentStep === 'business') return isBusinessValid;
    return false;
  };
  const handleNext = () => {
    if (!isCurrentStepValid()) return;
    if (currentStep === 'identity' && businessDocs.show) {
      setCurrentStep('business');
    } else {
      handleSubmit();
    }
  };
  const handleBack = () => {
    if (currentStep === 'identity') {
      onExit();
    } else {
      setCurrentStep('identity');
    }
  };
  const handleSubmit = () => {
    onComplete({
      idFront,
      idBack,
      bizDoc1,
      bizDoc2
    });
  };
  return <div className="flex flex-col h-full max-w-xl mx-auto pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-181">
      {/* Back link */}
      <button type="button" onClick={handleBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6 self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded" data-id="element-182">
        <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-183" />
        {currentStep === 'identity' ? 'Back to overview' : 'Back'}
      </button>

      {/* Header */}
      <header className="mb-6" data-id="element-184">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-185">
          {currentStep === 'identity' && 'Representative Identity'}
          {currentStep === 'business' && 'Business Registration'}
        </h1>
        <p className="text-base text-gray-600 leading-relaxed" data-id="element-186">
          {currentStep === 'identity' && 'Please provide clear, readable copies of your identity documents.'}
          {currentStep === 'business' && 'Please provide clear, readable copies of your business registration documents.'}
        </p>
      </header>

      <DemoFillBanner className="mb-10" message="Demo mode — auto-attach sample documents." onFill={() => {
      const docs = buildSampleDocuments();
      setIdFront(docs.idFront);
      setIdBack(docs.idBack);
      setBizDoc1(docs.bizDoc1);
      setBizDoc2(docs.bizDoc2);
    }} data-id="element-187" />

      <form onSubmit={e => {
      e.preventDefault();
      handleNext();
    }} className="flex-1 flex flex-col" data-id="element-188">
        <div className="space-y-12" data-id="element-189">
          {currentStep === 'identity' && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-190">
              {/* Group A: Representative Identity — always visible, copy adapts to chosen ID method */}
              <FormGroup title="Representative Identity" subtitle="Verify the person operating this account." data-id="element-191">
                {idMethod === 'passport' ? <FileUpload label="Passport (Photo Page)" description="Because you selected Passport in the previous step, please upload the photo page of your passport." value={idFront} onChange={setIdFront} required data-id="element-192" /> : <>
                    <FileUpload label="National ID (Front)" description={idMethod === 'national_id' ? 'Because you selected National ID in the previous step, please upload your MyKad, MyPR, Police, or Military ID.' : 'Upload the front of your MyKad, MyPR, Police, or Military ID.'} value={idFront} onChange={setIdFront} required data-id="element-193" />
                    <FileUpload label="National ID (Back)" description="Upload the back of your MyKad, MyPR, Police, or Military ID." value={idBack} onChange={setIdBack} required data-id="element-194" />
                  </>}
              </FormGroup>
            </div>}

          {currentStep === 'business' && businessDocs.show && <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-10" data-id="element-195">
              {/* Group B: Business Registration — conditional */}
              <FormGroup title="Business Registration" subtitle="Documents that prove your business is legally registered." data-id="element-196">
                <FileUpload label={businessDocs.doc1Label} value={bizDoc1} onChange={setBizDoc1} required data-id="element-197" />
                {businessDocs.doc2Label && <FileUpload label={businessDocs.doc2Label} value={bizDoc2} onChange={setBizDoc2} required={businessDocs.doc2Required} data-id="element-198" />}
              </FormGroup>
            </div>}

          {!businessType && currentStep === 'identity' && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" role="status" data-id="element-199">
              Complete <span className="font-semibold" data-id="element-200">Business Details</span>{' '}
              first so we can show the right documents to upload.
            </p>}
        </div>

        {/* Sticky footer — sits above the SplitLayout copyright, scrolls with column */}
        <div className="sticky bottom-0 left-0 right-0 -mx-2 mt-12 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-10" data-id="element-201">
          <div className="flex items-center justify-between gap-3 py-4 px-2" data-id="element-202">
            <Button variant="outline" type="button" onClick={handleBack} data-id="element-203">
              Back
            </Button>
            <Button type="submit" disabled={!isCurrentStepValid()} data-id="element-204">
              {currentStep === 'business' || !businessDocs.show ? 'Save & Continue' : 'Continue'}
            </Button>
          </div>
        </div>
      </form>
    </div>;
}